import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Switch,
  Tag,
  Select,
  Input,
  Button,
  message,
  Typography,
} from 'antd';

import AdminLayout from '../../../components/admin/AdminLayout';
import { adminApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import './adminCollections.css';

const { Paragraph } = Typography;

function formatUpdatedAt(value) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleString();
}

class AdminCollections extends Component {
  state = {
    loading: true,
    savingId: null,
    reorderSaving: false,
    draggingId: null,
    items: [],
    scopeFilter: 'all',
    searchQuery: '',
  };

  componentDidMount() {
    this.fetchCollections();
  }

  fetchCollections = async () => {
    this.setState({ loading: true });
    try {
      const { scopeFilter } = this.state;
      const params = {};

      params.visibility = 'public';
      if (scopeFilter === 'featured') {
        params.isFeatured = true;
      }

      const response = await adminApi.fetchCollections(params);
      const payload = response && response.data ? response.data : {};
      this.setState({
        loading: false,
        items: Array.isArray(payload.items) ? payload.items : [],
      });
    } catch (error) {
      this.setState({
        loading: false,
        items: [],
      });
      message.error('Unable to load templates.');
    }
  };

  handleCollectionUpdate = async (record, patch) => {
    this.setState({ savingId: record.id });
    try {
      const response = await adminApi.updateCollection(record.id, patch);
      const updated = response && response.data ? response.data : response;

      this.setState((prevState) => ({
        savingId: null,
        items: prevState.items.map((item) =>
          item.id === record.id
            ? {
                ...item,
                ...updated,
              }
            : item,
        ),
      }));
      message.success('Template updated.');
    } catch (error) {
      this.setState({ savingId: null });
      message.error('Unable to update template.');
    }
  };

  handleFeaturedReorder = async (nextItems) => {
    const reorderedItems = nextItems.map((item, index) => ({
      ...item,
      featuredOrder: index + 1,
    }));

    this.setState({
      reorderSaving: true,
      items: reorderedItems,
      draggingId: null,
    });

    try {
      await adminApi.reorderCollections(
        reorderedItems.map((item) => ({
          id: item.id,
          featuredOrder: item.featuredOrder,
        })),
      );
      this.setState({ reorderSaving: false });
      message.success('Featured template order updated.');
    } catch (error) {
      this.setState({ reorderSaving: false, draggingId: null });
      message.error('Unable to reorder featured templates.');
      this.fetchCollections();
    }
  };

  moveDraggedItem = (targetId) => {
    this.setState((prevState) => {
      const { draggingId, items } = prevState;
      if (!draggingId || draggingId === targetId) {
        return null;
      }

      const sourceIndex = items.findIndex((item) => item.id === draggingId);
      const targetIndex = items.findIndex((item) => item.id === targetId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return null;
      }

      const nextItems = items.slice();
      const [dragged] = nextItems.splice(sourceIndex, 1);
      nextItems.splice(targetIndex, 0, dragged);

      return {
        items: nextItems,
      };
    });
  };

  render() {
    const { admin, onLogout } = this.props;
    const isViewer = admin && admin.user && admin.user.type === 'viewer';
    const {
      loading,
      savingId,
      reorderSaving,
      draggingId,
      items,
      scopeFilter,
      searchQuery,
    } = this.state;

    const normalizedSearch = searchQuery.trim().toLowerCase();
    const filteredItems = normalizedSearch
      ? items.filter((item) => String(item.title || '').toLowerCase().includes(normalizedSearch))
      : items;

    const featuredItems = filteredItems.filter((item) => Boolean(item.isFeatured));

    const columns = [
      {
        title: 'Template',
        key: 'collection',
        render: (value, record) => (
          <div className="admin-collections__collection">
            <div className="admin-collections__thumb">
              <img src={record.latestImageUrl || record.coverImageUrl} alt={record.title} />
            </div>
            <div>
              <strong>{record.title}</strong>
              <Paragraph ellipsis={{ rows: 2 }} className="admin-collections__description">
                {record.description || 'No description'}
              </Paragraph>
            </div>
          </div>
        ),
      },
      {
        title: 'Owner',
        key: 'owner',
        render: (value, record) =>
          (record.createdBy && (record.createdBy.name || record.createdBy.email)) || 'Unknown',
      },
      {
        title: 'Tags',
        key: 'tags',
        render: (value, record) => (
          <div className="admin-collections__tags">
            {(Array.isArray(record.tags) ? record.tags : []).slice(0, 3).map((tag) => (
              <Tag key={tag.id || tag.name}>{tag.name || tag}</Tag>
            ))}
          </div>
        ),
      },
      {
        title: 'Featured',
        key: 'featured',
        render: (value, record) => (isViewer
          ? <Tag color={record.isFeatured ? 'blue' : 'default'}>{record.isFeatured ? 'Featured' : 'Hidden'}</Tag>
          : (
            <Switch
              checked={Boolean(record.isFeatured)}
              checkedChildren="Featured"
              unCheckedChildren="Hidden"
              loading={savingId === record.id}
              onChange={(checked) => this.handleCollectionUpdate(record, { isFeatured: checked })}
            />
          )),
      },
      {
        title: 'Updated',
        key: 'updatedAt',
        render: (value, record) => (
          <span className="admin-collections__date">{formatUpdatedAt(record.updatedAt)}</span>
        ),
      },
    ];

    return (
      <AdminLayout
        title="Templates"
        subtitle="Manage which public templates appear in the catalog experience."
        activeKey="templates"
        admin={admin}
        onLogout={onLogout}
      >
        <div className="admin-collections__toolbar">
          <Select
            value={scopeFilter}
            style={{ width: 180 }}
            onChange={(value) => this.setState({ scopeFilter: value }, this.fetchCollections)}
            options={[
              { label: 'All Templates', value: 'all' },
              { label: 'Featured Templates', value: 'featured' },
            ]}
          />
          <Input
            allowClear
            value={searchQuery}
            placeholder="Search template name"
            className="admin-collections__search"
            onChange={(event) => this.setState({ searchQuery: event.target.value })}
          />
          <Button onClick={this.fetchCollections}>Refresh</Button>
        </div>
        {scopeFilter === 'featured' ? (
          <div className="admin-collections__featured">
            {!isViewer && <p className="admin-collections__hint">
              Drag public featured templates to change their order in the catalog.
            </p>}
            <div className="admin-collections__featured-list">
              {featuredItems.map((item) => (
                <div
                  key={item.id}
                  className={`admin-collections__featured-item${
                    draggingId === item.id ? ' is-dragging' : ''
                  }`}
                  draggable={!isViewer && !reorderSaving}
                  onDragStart={() => { if (!isViewer) this.setState({ draggingId: item.id }); }}
                  onDragOver={(event) => {
                    if (isViewer) return;
                    event.preventDefault();
                    if (!reorderSaving) {
                      this.moveDraggedItem(item.id);
                    }
                  }}
                  onDragEnd={() => this.setState({ draggingId: null })}
                  onDrop={(event) => {
                    if (isViewer) return;
                    event.preventDefault();
                    if (!reorderSaving) {
                      this.handleFeaturedReorder(this.state.items.filter(
                        (entry) => Boolean(entry.isFeatured),
                      ));
                    }
                  }}
                >
                  {!isViewer && <div className="admin-collections__featured-handle" aria-hidden="true">
                    <i className="fa-solid fa-grip-vertical" />
                  </div>}
                  <div className="admin-collections__thumb">
                    <img src={item.latestImageUrl || item.coverImageUrl} alt={item.title} />
                  </div>
                  <div className="admin-collections__featured-copy">
                    <strong>{item.title}</strong>
                    <Paragraph ellipsis={{ rows: 2 }} className="admin-collections__description">
                      {item.description || 'No description'}
                    </Paragraph>
                    <div className="admin-collections__featured-meta">
                      <Tag color="blue">Template</Tag>
                      <span className="admin-collections__date">{formatUpdatedAt(item.updatedAt)}</span>
                    </div>
                  </div>
                  <Tag color="blue">#{item.featuredOrder || 0}</Tag>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredItems}
            pagination={{ pageSize: 20 }}
          />
        )}
      </AdminLayout>
    );
  }
}

AdminCollections.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  }),
  onLogout: PropTypes.func.isRequired,
};

AdminCollections.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminCollections);
